import { Chip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import noRequestImg from "../../../assets/images/placeholders/noProductImagePlacholder.svg";
import messages from "../../../assets/locale/messages";
import ViewDeactivateButton from "../../../components/ViewDeactivateButton";
import ViewEditButton from "../../../components/ViewEditButton";
import ViewReactivateButton from "../../../components/ViewReactivateButton";
import {
  getEmployeeDetailsRequest,
  getEmployeeDetailsResponse
} from "../../../store/Employees/actions";
import { CHIPS_CLASSES } from "../../../utils/Constants";
import { EmployeesIcon } from "../../../utils/Icons/SidemenuIcons";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import DeactivateEmployeeModal from "../DeactivateEmployee";
import ReactivateEmployeeModal from "../ReactivateEmployee";
import { hasPermission, PERMISSIONS_IDS } from "../../../utils/Roles";
import { generateUniqueSrc } from "../../../utils/Helpers";
import "./EmployeeDetails.scss";

const EmployeeDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [
    deactivateEmployeeModalOpen,
    setDeactivateEmployeeModalOpen
  ] = useState(false);
  const [activateEmployeeModalOpen, setActivateEmployeeModalOpen] =
    useState(false);

  const lang = useSelector((state) => state.locale.lang);
  const employeeDetails = useSelector(
    (state) => state.employees.employeeDetails
  );
  const { employees, shared } = messages[lang];

  const handleDeactivateEmployee = () => {
    setDeactivateEmployeeModalOpen(true);
  };
  const handleReactivateEmployee = () => {
    setActivateEmployeeModalOpen(true);
  };

  const handleCloseModal = () => {
    setDeactivateEmployeeModalOpen(false);
    setActivateEmployeeModalOpen(false);
  };

  useEffect(() => {
    if (id) {
      employeeDetails.Image = noRequestImg; // or use an empty string
      dispatch(getEmployeeDetailsRequest(id));
    }
    return () => {
      dispatch(getEmployeeDetailsResponse({}));
      // Reset the image source when the component unmounts
      employeeDetails.Image = noRequestImg; // or use an empty string
    };
  }, [dispatch, id]);

  const handleStatusClass = (status) => {
    let chipClass;
    if (status === employees.status.active) {
      chipClass = CHIPS_CLASSES.green;
    } else {
      chipClass = CHIPS_CLASSES.lightBrown;
    }
    return chipClass;
  };

  const handleEditEmployee = () => {
    navigate(ROUTE_PATHS.editEmployee?.replace(":id", id), {
      state: { isRequest: true }
    });
  };

  const chipClass = handleStatusClass(employeeDetails?.Status);

  const onImageError = (e) => {
    e.target.src = noRequestImg;
  };

  const renderLabelValue = (label, value) => (
    <div className="d-md-flex align-items-md-center align-items-start text-break">
      <span className="d-block mb-3 light-brown col-xl-3 col-lg-4 col-md-4 text-placeholder">{`${label} : `}</span>
      <span className="d-block mb-3 text-color col-xl-9 col-lg-8 col-md-8 font-medium text-break ms-md-3 ms-0 mt-md-0 mt-3">
        {value}
      </span>
    </div>
  );

  return (
    <div className="employee-details-container">
      <div className=" d-flex align-items-center justify-content-between mb-4 ">
        <Typography variant="h5" className="title-color ">
          {employees.employeeDetails.employeeDetails}
        </Typography>
        {employeeDetails && !!Object.keys(employeeDetails).length && (
          <div className="title-buttons-container d-flex gap-sm-3 gap-1 align-items-center">
            {hasPermission(PERMISSIONS_IDS.employees_edit) && (
              <ViewEditButton
                label={shared.edit}
                onClick={handleEditEmployee}
              />
            )}
            {hasPermission(
              PERMISSIONS_IDS.employees_activate_deactivate
            ) && (
              <>
                {employeeDetails?.Status ===
                employees.status.active ? (
                  <ViewDeactivateButton
                    label={shared.deactivate}
                    onClick={handleDeactivateEmployee}
                  />
                ) : (
                  <ViewReactivateButton
                    label={shared.reactivate}
                    onClick={handleReactivateEmployee}
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>

      {employeeDetails && !!Object.keys(employeeDetails).length && (
        <main className="white-bg p-4 border-radius-10 light-border w-100">
          <div className="row p-md-0 p-2">
            <div className="col-xl-3 col-md-4 left-side mb-4 mb-md-0 pb-4 pb-md-0">
              <div className="text-center d-flex flex-column">
                <div className="mb-4">
                  <img
                    src={generateUniqueSrc(
                      employeeDetails?.Image || noRequestImg
                    )}
                    onError={onImageError}
                    alt={"Employee-Image"}
                    className="employee-details-img light-border border-radius-10 object-fit-cover"
                  />
                </div>
                <Typography
                  variant="h6"
                  className="title-color mb-4 w-100 text-break">
                  {employeeDetails?.Name}
                </Typography>
                <div className="d-flex justify-content-center">
                  <div className="col-1 me-2">
                    <EmployeesIcon />
                  </div>
                  <p className="text-color m-0 text-break roleName-container">
                    {employeeDetails?.RoleName}
                  </p>
                </div>
              </div>
            </div>
            <div className="offset-xl-0 ps-xl-5 ps-md-5 ms-md-0 offset-md-1 col-xl-9 col-md-8 p-0">
              <div className="d-flex flex-column gap-2">
                <Typography variant="h6" className="title-color mb-3">
                  {employees.employeeDetails.employeeInfo}
                </Typography>
                {renderLabelValue(
                  employees.employeeDetails.emailAddress,
                  employeeDetails?.Email
                )}
                {renderLabelValue(
                  employees.employeeDetails.contactNumber,
                  `${
                    employeeDetails?.MobileNumber &&
                    !!employeeDetails?.MobileNumber?.length
                      ? `+${employeeDetails.MobileCode}`
                      : ""
                  } ${
                    employeeDetails?.MobileNumber &&
                    !!employeeDetails?.MobileNumber?.length
                      ? employeeDetails?.MobileNumber
                      : "--"
                  } `
                )}
                {renderLabelValue(
                  employees.employeeDetails.status,
                  <Chip
                    label={employeeDetails?.Status}
                    className={`border-radius-8 fw-bold ${chipClass}`}
                    sx={{ fontWeight: "semibold" }}
                  />
                )}
              </div>
            </div>
          </div>
        </main>
      )}

      {deactivateEmployeeModalOpen && (
        <DeactivateEmployeeModal
          open={deactivateEmployeeModalOpen}
          closeModal={handleCloseModal}
          employeeInfo={{ ...employeeDetails, id }}
        />
      )}
      {activateEmployeeModalOpen && (
        <ReactivateEmployeeModal
          open={activateEmployeeModalOpen}
          closeModal={handleCloseModal}
          employeeInfo={{ ...employeeDetails, id }}
        />
      )}
    </div>
  );
};

export default EmployeeDetails;
